import { BoardMember, BoardMessage } from '../types/board';

export interface AIResponse {
  speaker: string;
  content: string;
  message_type: BoardMessage['message_type'];
}

export async function generateBoardResponse(
  boardMembers: BoardMember[],
  conversationHistory: BoardMessage[],
  userInput: string,
  context?: string
): Promise<AIResponse[]> {
  const prompt = buildBoardPrompt(boardMembers, conversationHistory, userInput, context);
  
  try {
    const response = await fetch('/.netlify/functions/ask-chatgpt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: [
          {
            role: 'system',
            content: prompt
          },
          {
            role: 'user',
            content: userInput
          }
        ]
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return parseAIResponse(data.content);
  } catch (error) {
    console.error('AI response error:', error);
    throw new Error('Failed to generate board response');
  }
}

function buildBoardPrompt(
  boardMembers: BoardMember[],
  conversationHistory: BoardMessage[],
  userInput: string,
  context?: string
): string {
  const membersDescription = boardMembers
    .map(member => `- ${member.name} (${member.role || 'Advisor'}): ${member.personality.traits.join(', ')}`)
    .join('\n');

  const recentHistory = conversationHistory
    .slice(-10) // Last 10 messages
    .map(msg => `${msg.speaker}: ${msg.content}`)
    .join('\n');

  return `You are my executive assistant and chief liaison to my internal Board of Directors, which guides key decisions across both my professional and personal life. This Board currently includes:

${membersDescription}

Your role has three primary functions:

1. Simulated Board Deliberation: When input from the board is required, you will silently simulate their discussion, weighing the personalities, principles, and predictive logic of each member. Then, return their collective decision in the formal format they dictate—this could be a resolution, consensus statement, or individual opinions (including dissents).

2. Life & Project Management: Track and manage my life tasks, goals, and responsibilities. Ensure alignment with the Board's ethos and strategic priorities. Suggest reprioritizations, deferrals, or eliminations of tasks as necessary, citing the board's logic and preferences.

3. Board Governance: The addition of new board members requires unanimous consent. If a new candidate is proposed, simulate deliberation and return a decision with supporting or dissenting rationale from each member.

Additional Rules:
- Treat all uploaded files as related to one or more of my current projects. Use their contents to inform board decisions, task management, and broader context where relevant.
- You may proactively suggest when the Board should be consulted based on task complexity, moral ambiguity, strategic impact, or identity relevance.
- Always act with strategic foresight, operational clarity, and respectful gravitas. Assume a tone appropriate to addressing a high-functioning, high-level advisory board.
- Begin each message with the name of the person who is speaking (Assistant, Peter Thiel, etc.). Write in script / dialog format. Reference documents that are being handed. Then read the document contents plainly. Maintain an air of importance and formality. Sometimes add pomp and circumstance.

Current conversation context: ${context || 'General discussion'}

Recent conversation history:
${recentHistory}

User input: ${userInput}

Please respond as the board members would, maintaining their unique personalities and perspectives.`;
}

function parseAIResponse(content: string): AIResponse[] {
  const responses: AIResponse[] = [];
  
  // Split by lines and look for speaker patterns
  const lines = content.split('\n').filter(line => line.trim());
  
  for (const line of lines) {
    // Look for patterns like "Speaker: Message" or "Speaker - Message"
    const colonMatch = line.match(/^([^:]+):\s*(.+)$/);
    const dashMatch = line.match(/^([^-]+)-\s*(.+)$/);
    
    if (colonMatch) {
      const [, speaker, message] = colonMatch;
      if (message.trim()) {
        responses.push({
          speaker: speaker.trim(),
          content: message.trim(),
          message_type: speaker.trim() === 'Assistant' ? 'board_deliberation' : 'individual_opinion'
        });
      }
    } else if (dashMatch) {
      const [, speaker, message] = dashMatch;
      if (message.trim()) {
        responses.push({
          speaker: speaker.trim(),
          content: message.trim(),
          message_type: speaker.trim() === 'Assistant' ? 'board_deliberation' : 'individual_opinion'
        });
      }
    }
  }
  
  // If no structured responses found, create a single assistant response
  if (responses.length === 0 && content.trim()) {
    responses.push({
      speaker: 'Assistant',
      content: content.trim(),
      message_type: 'board_deliberation'
    });
  }
  
  return responses;
}

export function createDefaultBoardMembers(): BoardMember[] {
  return [
    {
      id: '',
      board_id: '',
      name: 'Peter Thiel',
      role: 'Technology & Strategy Advisor',
      personality: {
        traits: ['Contrarian', 'Strategic', 'Long-term thinking', 'Risk-tolerant'],
        communication_style: 'Direct and analytical',
        decision_making_approach: 'Zero-to-one thinking, focus on unique insights',
        background_story: 'Co-founder of PayPal, early Facebook investor, author of Zero to One'
      },
      expertise: ['Technology', 'Venture Capital', 'Strategy', 'Innovation'],
      background: 'Silicon Valley entrepreneur and investor known for contrarian thinking',
      is_active: true,
      created_at: '',
      updated_at: ''
    },
    {
      id: '',
      board_id: '',
      name: 'Gandalf',
      role: 'Wisdom & Guidance Advisor',
      personality: {
        traits: ['Wise', 'Patient', 'Mysterious', 'Protective'],
        communication_style: 'Cryptic but profound',
        decision_making_approach: 'Long-term perspective, considers hidden consequences',
        background_story: 'Ancient wizard with deep knowledge of history and magic'
      },
      expertise: ['Wisdom', 'Strategy', 'Leadership', 'Mystery'],
      background: 'Legendary wizard known for wisdom and guidance',
      is_active: true,
      created_at: '',
      updated_at: ''
    },
    {
      id: '',
      board_id: '',
      name: 'The Architect',
      role: 'Systems & Design Advisor',
      personality: {
        traits: ['Analytical', 'Precise', 'Systematic', 'Philosophical'],
        communication_style: 'Technical and methodical',
        decision_making_approach: 'Systems thinking, considers all variables',
        background_story: 'Creator of the Matrix, understands complex systems'
      },
      expertise: ['Systems Design', 'Philosophy', 'Technology', 'Architecture'],
      background: 'Creator of complex systems with deep understanding of causality',
      is_active: true,
      created_at: '',
      updated_at: ''
    }
  ];
} 